import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Broadcast, X, Image as ImageIcon, Camera, UploadSimple } from "@phosphor-icons/react";
import { PRESET_WALLPAPERS } from "./goLiveData";

export default function GoLiveModal({ open, onClose, onLive }) {
    const [topic, setTopic] = useState("");
    const [useWp, setUseWp] = useState(false);
    const [wpUrl, setWpUrl] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [uploadingWp, setUploadingWp] = useState(false);
    const [eligibility, setEligibility] = useState(null);

    useEffect(() => {
        if (!open) return;
        api.get("/live-eligibility").then(({ data }) => setEligibility(data)).catch(() => {});
    }, [open]);

    if (!open) return null;

    const onUploadWp = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) return toast.error("Pick an image");
        if (f.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
        setUploadingWp(true);
        try {
            const form = new FormData();
            form.append("file", f);
            const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            setWpUrl(url);
            toast.success("Wallpaper uploaded");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setUploadingWp(false);
        }
    };

    const start = async () => {
        if (!topic.trim()) {
            toast.error("Add a topic so viewers know what's up");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/live/start", {
                topic: topic.trim(),
                wallpaper_url: useWp ? wpUrl : null,
                using_wallpaper: useWp && !!wpUrl,
            });
            toast.success("You're live!");
            onLive(data);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail) || "Could not go live");
        } finally {
            setSubmitting(false);
        }
    };

    const blocked = eligibility && !eligibility.eligible;

    return (
        <div data-testid="go-live-modal" className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-wta-surface border border-wta-border rounded-sm w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-wta-border">
                    <div className="flex items-center gap-2">
                        <Broadcast size={18} weight="fill" className="text-wta-primary" />
                        <div className="font-heading text-lg tracking-wider uppercase">Go Live</div>
                    </div>
                    <button onClick={onClose} aria-label="Close" data-testid="go-live-close" className="text-white/60 hover:text-white"><X size={18} /></button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Eligibility */}
                    {eligibility && (
                        <div data-testid="eligibility-banner" className={`text-xs rounded-sm px-3 py-2 border ${eligibility.eligible ? "bg-wta-primary/10 border-wta-primary/40 text-wta-primary" : "bg-red-500/10 border-red-500/40 text-red-300"}`}>
                            {eligibility.live_disabled ? (
                                <>Streaming suspended: {eligibility.live_disabled_reason || "policy violation"}. Submit an appeal in your profile.</>
                            ) : eligibility.verified_followers === -1 ? (
                                <>Admin override: live access enabled.</>
                            ) : (
                                <>Face-verified followers: <b>{eligibility.verified_followers}</b> / {eligibility.required} required to go live.</>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="text-xs tracking-widest text-wta-muted uppercase">Live topic</label>
                        <input
                            data-testid="go-live-topic"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            maxLength={140}
                            placeholder="What's the stream about?"
                            className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm"
                            autoFocus
                        />
                        <div className="text-[10px] text-wta-muted mt-1">{topic.length}/140</div>
                    </div>

                    <div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                            <input
                                data-testid="go-live-use-wallpaper"
                                type="checkbox"
                                checked={useWp}
                                onChange={(e) => setUseWp(e.target.checked)}
                                className="w-4 h-4 accent-wta-primary"
                            />
                            <ImageIcon size={14} /> Use a wallpaper instead of camera
                        </label>
                        {useWp && (
                            <div className="mt-3 space-y-3">
                                <div className="grid grid-cols-4 gap-2">
                                    {PRESET_WALLPAPERS.map((w) => (
                                        <button
                                            key={w.id}
                                            type="button"
                                            data-testid={`wp-preset-${w.id}`}
                                            onClick={() => setWpUrl(w.url)}
                                            className={`aspect-video rounded-sm overflow-hidden border-2 ${wpUrl === w.url ? "border-wta-primary" : "border-wta-border"}`}
                                        >
                                            <img src={w.url} alt={w.label} className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        data-testid="go-live-wp-url"
                                        value={wpUrl}
                                        onChange={(e) => setWpUrl(e.target.value)}
                                        placeholder="Paste image URL or upload →"
                                        className="flex-1 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-xs"
                                    />
                                    <label className="btn-ghost px-3 py-2 rounded-sm text-xs flex items-center gap-1 cursor-pointer">
                                        <UploadSimple size={12} /> {uploadingWp ? "…" : "Upload"}
                                        <input data-testid="go-live-wp-upload" type="file" accept="image/*" onChange={onUploadWp} className="hidden" />
                                    </label>
                                </div>
                                {wpUrl && (
                                    <img src={wpUrl} alt="Selected wallpaper" className="w-full aspect-video object-cover rounded-sm border border-wta-border" />
                                )}
                                <div className="text-[10px] text-wta-muted flex items-center gap-1">
                                    <Camera size={11} /> Camera will stay off while a wallpaper is selected.
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-5 py-4 border-t border-wta-border flex items-center justify-end gap-2">
                    <button onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-sm font-heading uppercase tracking-wider">Cancel</button>
                    <button
                        data-testid="go-live-confirm"
                        onClick={start}
                        disabled={submitting || blocked}
                        className="btn-primary px-5 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-40"
                    >
                        <Broadcast size={14} weight="fill" /> {submitting ? "Starting…" : "Go Live"}
                    </button>
                </div>
            </div>
        </div>
    );
}

GoLiveModal.PRESET_WALLPAPERS = PRESET_WALLPAPERS;
export { PRESET_WALLPAPERS };